import { Assert } from "../utils";
import { Cartesian3 } from "./geometry.cartesian";
class Face {
    constructor(v0, v1, v2, o0, o1, o2, normal) {
        this.Vertex0 = v0;
        this.Vertex1 = v1;
        this.Vertex2 = v2;
        this.Opposite0 = o0;
        this.Opposite1 = o1;
        this.Opposite2 = o2;
        this.Normal = normal;
    }
    Equals(other) {
        return (this.Vertex0 == other.Vertex0 &&
            this.Vertex1 == other.Vertex1 &&
            this.Vertex2 == other.Vertex2 &&
            this.Opposite0 == other.Opposite0 &&
            this.Opposite1 == other.Opposite1 &&
            this.Opposite2 == other.Opposite2 &&
            this.Normal == other.Normal);
    }
}
class PointFace {
    constructor(p, f, d) {
        this.Point = p;
        this.Face = f;
        this.Distance = d;
    }
}
class HorizonEdge {
    constructor(Face = 0, Edge0 = 0, Edge1 = 0) {
        this.Face = Face;
        this.Edge0 = Edge0;
        this.Edge1 = Edge1;
    }
}
export class QuickHull {
    constructor() {
        this.openSetTail = -1;
        this.faceCount = 0;
        this.faces = new Map();
        this.litFaces = new Set();
        this.horizon = new Array();
        this.openSet = new Array();
        this.hullVerts = new Map();
    }
    generateHull(points, splitVerts = false) {
        if (points.length < 4) {
            throw new Error("Need at least 4 points to generate a convex hull");
        }
        this._initialize(points, splitVerts);
        this._generateInitialHull(points);
        while (this.openSetTail >= 0) {
            this._growHull(points);
        }
        const exported = this._exportMeshData(points, splitVerts);
        return exported;
    }
    _initialize(points, splitVerts) {
        this.faceCount = 0;
        this.openSetTail = -1;
        this.faces.clear();
        this.litFaces?.clear();
        this.horizon = [];
        this.openSet = [];
        if (!splitVerts) {
            this.hullVerts.clear();
        }
    }
    _generateInitialHull(points) {
        const b = this._findInitialHullIndices(points);
        const b0 = b[0];
        const b1 = b[1];
        const b2 = b[2];
        const b3 = b[3];
        var v0 = points[b0];
        var v1 = points[b1];
        var v2 = points[b2];
        var v3 = points[b3];
        var above = Cartesian3.Dot(Cartesian3.Subtract(v3, v1), Cartesian3.Cross(Cartesian3.Subtract(v1, v0), Cartesian3.Subtract(v2, v0))) > 0.0;
        this.faceCount = 0;
        if (above) {
            this.faces.set(this.faceCount++, new Face(b0, b2, b1, 3, 1, 2, Cartesian3.Normal(points[b0], points[b2], points[b1])));
            this.faces.set(this.faceCount++, new Face(b0, b1, b3, 3, 2, 0, Cartesian3.Normal(points[b0], points[b1], points[b3])));
            this.faces.set(this.faceCount++, new Face(b0, b3, b2, 3, 0, 1, Cartesian3.Normal(points[b0], points[b3], points[b2])));
            this.faces.set(this.faceCount++, new Face(b1, b2, b3, 2, 1, 0, Cartesian3.Normal(points[b1], points[b2], points[b3])));
        }
        else {
            this.faces.set(this.faceCount++, new Face(b0, b1, b2, 3, 2, 1, Cartesian3.Normal(points[b0], points[b1], points[b2])));
            this.faces.set(this.faceCount++, new Face(b0, b3, b1, 3, 0, 2, Cartesian3.Normal(points[b0], points[b3], points[b1])));
            this.faces.set(this.faceCount++, new Face(b0, b2, b3, 3, 1, 0, Cartesian3.Normal(points[b0], points[b2], points[b3])));
            this.faces.set(this.faceCount++, new Face(b1, b3, b2, 2, 0, 1, Cartesian3.Normal(points[b1], points[b3], points[b2])));
        }
        this._verifyFaces(points);
        for (let i = 0; i < points.length; i++) {
            if (i == b0 || i == b1 || i == b2 || i == b3)
                continue;
            this.openSet.push(new PointFace(i, QuickHull.UNASSIGNED, 0.0));
        }
        this.openSet.push(new PointFace(b0, QuickHull.INSIDE, Number.NaN));
        this.openSet.push(new PointFace(b1, QuickHull.INSIDE, Number.NaN));
        this.openSet.push(new PointFace(b2, QuickHull.INSIDE, Number.NaN));
        this.openSet.push(new PointFace(b3, QuickHull.INSIDE, Number.NaN));
        this.openSetTail = this.openSet.length - 5;
        Assert(this.openSet.length == points.length);
        for (let i = 0; i <= this.openSetTail; i++) {
            Assert(this.openSet[i].Face == QuickHull.UNASSIGNED);
            Assert(this.openSet[this.openSetTail].Face == QuickHull.UNASSIGNED);
            Assert(this.openSet[this.openSetTail + 1].Face == QuickHull.INSIDE);
            var assigned = false;
            var fp = this.openSet[i];
            Assert(this.faces.size == 4);
            Assert(this.faces.size == this.faceCount);
            for (let j = 0; j < 4; j++) {
                Assert(this.faces.has(j));
                var face = this.faces.get(j);
                var dist = this._pointFaceDistance(points[fp.Point], points[face.Vertex0], face);
                if (dist > 0) {
                    fp.Face = j;
                    fp.Distance = dist;
                    this.openSet[i] = fp;
                    assigned = true;
                    break;
                }
            }
            if (!assigned) {
                fp.Face = QuickHull.INSIDE;
                fp.Distance = Number.NaN;
                this.openSet[i] = this.openSet[this.openSetTail];
                this.openSet[this.openSetTail] = fp;
                this.openSetTail -= 1;
                i -= 1;
            }
        }
        this._verifyOpenSet(points);
    }
    _findInitialHullIndices(points) {
        var count = points.length;
        for (let i0 = 0; i0 < count - 3; i0++) {
            for (let i1 = i0 + 1; i1 < count - 2; i1++) {
                var p0 = points[i0];
                var p1 = points[i1];
                if (Cartesian3.AreCoincident(p0, p1, QuickHull.EPSILON))
                    continue;
                for (let i2 = i1 + 1; i2 < count - 1; i2++) {
                    var p2 = points[i2];
                    if (Cartesian3.AreCollinear(p0, p1, p2, QuickHull.EPSILON))
                        continue;
                    for (let i3 = i2 + 1; i3 < count - 0; i3++) {
                        var p3 = points[i3];
                        if (Cartesian3.AreCoplanar(p0, p1, p2, p3, QuickHull.EPSILON))
                            continue;
                        return [i0, i1, i2, i3];
                    }
                }
            }
        }
        throw new Error("Can't generate hull, points are coplanar");
    }
    _growHull(points) {
        Assert(this.openSetTail >= 0);
        Assert(this.openSet[0].Face != QuickHull.INSIDE);
        var farthestPoint = 0;
        var dist = this.openSet[0].Distance;
        for (let i = 1; i <= this.openSetTail; i++) {
            if (this.openSet[i].Distance > dist) {
                farthestPoint = i;
                dist = this.openSet[i].Distance;
            }
        }
        this._findHorizon(points, points[this.openSet[farthestPoint].Point], this.openSet[farthestPoint].Face, this.faces.get(this.openSet[farthestPoint].Face));
        this._verifyHorizon();
        this._constructCone(points, this.openSet[farthestPoint].Point);
        this._verifyFaces(points);
        this._reassignPoints(points);
    }
    _findHorizon(points, point, fi, face) {
        Assert(face != null && face != undefined);
        this.litFaces.clear();
        this.horizon = [];
        this.litFaces.add(fi);
        Assert(this._pointFaceDistance(point, points[face.Vertex0], face) > 0.0);
        {
            var oppositeFace = this.faces.get(face.Opposite0);
            var dist = this._pointFaceDistance(point, points[oppositeFace.Vertex0], oppositeFace);
            if (dist <= 0.0) {
                this.horizon.push(new HorizonEdge(face.Opposite0, face.Vertex1, face.Vertex2));
            }
            else {
                this._searchHorizon(points, point, fi, face.Opposite0, oppositeFace);
            }
        }
        if (!this.litFaces.has(face.Opposite1)) {
            var oppositeFace = this.faces.get(face.Opposite1);
            var dist = this._pointFaceDistance(point, points[oppositeFace.Vertex0], oppositeFace);
            if (dist <= 0.0) {
                this.horizon.push(new HorizonEdge(face.Opposite1, face.Vertex2, face.Vertex0));
            }
            else {
                this._searchHorizon(points, point, fi, face.Opposite1, oppositeFace);
            }
        }
        if (!this.litFaces.has(face.Opposite2)) {
            var oppositeFace = this.faces.get(face.Opposite2);
            var dist = this._pointFaceDistance(point, points[oppositeFace.Vertex0], oppositeFace);
            if (dist <= 0.0) {
                this.horizon.push(new HorizonEdge(face.Opposite2, face.Vertex0, face.Vertex1));
            }
            else {
                this._searchHorizon(points, point, fi, face.Opposite2, oppositeFace);
            }
        }
    }
    _searchHorizon(points, point, prevFaceIndex, faceCount, face) {
        Assert(prevFaceIndex >= 0);
        Assert(this.litFaces.has(prevFaceIndex));
        Assert(!this.litFaces.has(faceCount));
        Assert(this.faces.get(faceCount)?.Equals(face));
        this.litFaces.add(faceCount);
        let nextFaceIndex0;
        let nextFaceIndex1;
        let edge0;
        let edge1;
        let edge2;
        if (prevFaceIndex == face.Opposite0) {
            nextFaceIndex0 = face.Opposite1;
            nextFaceIndex1 = face.Opposite2;
            edge0 = face.Vertex2;
            edge1 = face.Vertex0;
            edge2 = face.Vertex1;
        }
        else if (prevFaceIndex == face.Opposite1) {
            nextFaceIndex0 = face.Opposite2;
            nextFaceIndex1 = face.Opposite0;
            edge0 = face.Vertex0;
            edge1 = face.Vertex1;
            edge2 = face.Vertex2;
        }
        else {
            Assert(prevFaceIndex == face.Opposite2);
            nextFaceIndex0 = face.Opposite0;
            nextFaceIndex1 = face.Opposite1;
            edge0 = face.Vertex1;
            edge1 = face.Vertex2;
            edge2 = face.Vertex0;
        }
        if (!this.litFaces.has(nextFaceIndex0)) {
            const oppositeFace = this.faces.get(nextFaceIndex0);
            const dist = this._pointFaceDistance(point, points[oppositeFace.Vertex0], oppositeFace);
            if (dist <= 0.0) {
                this.horizon.push(new HorizonEdge(nextFaceIndex0, edge0, edge1));
            }
            else {
                this._searchHorizon(points, point, faceCount, nextFaceIndex0, oppositeFace);
            }
        }
        if (!this.litFaces.has(nextFaceIndex1)) {
            const oppositeFace = this.faces.get(nextFaceIndex1);
            const dist = this._pointFaceDistance(point, points[oppositeFace.Vertex0], oppositeFace);
            if (dist <= 0.0) {
                this.horizon.push(new HorizonEdge(nextFaceIndex1, edge1, edge2));
            }
            else {
                this._searchHorizon(points, point, faceCount, nextFaceIndex1, oppositeFace);
            }
        }
    }
    _constructCone(points, farthestPoint) {
        for (let fi of this.litFaces) {
            Assert(this.faces.has(fi));
            this.faces.delete(fi);
        }
        const firstNewFace = this.faceCount;
        for (let i = 0; i < this.horizon.length; i++) {
            const v0 = farthestPoint;
            const v1 = this.horizon[i].Edge0;
            const v2 = this.horizon[i].Edge1;
            const o0 = this.horizon[i].Face;
            const o1 = i == this.horizon.length - 1 ? firstNewFace : firstNewFace + i + 1;
            const o2 = i == 0 ? firstNewFace + this.horizon.length - 1 : firstNewFace + i - 1;
            let fi = this.faceCount++;
            this.faces.set(fi, new Face(v0, v1, v2, o0, o1, o2, Cartesian3.Normal(points[v0], points[v1], points[v2])));
            var horizonFace = this.faces.get(this.horizon[i].Face);
            if (horizonFace.Vertex0 == v1) {
                Assert(v2 == horizonFace.Vertex2);
                horizonFace.Opposite1 = fi;
            }
            else if (horizonFace.Vertex1 == v1) {
                Assert(v2 == horizonFace.Vertex0);
                horizonFace.Opposite2 = fi;
            }
            else {
                Assert(v1 == horizonFace.Vertex2);
                Assert(v2 == horizonFace.Vertex1);
                horizonFace.Opposite0 = fi;
            }
            this.faces.set(this.horizon[i].Face, horizonFace);
        }
    }
    _reassignPoints(points) {
        for (let i = 0; i <= this.openSetTail; i++) {
            var fp = this.openSet[i];
            if (this.litFaces.has(fp.Face)) {
                var assigned = false;
                var point = points[fp.Point];
                for (let [fi, face] of this.faces) {
                    var dist = this._pointFaceDistance(point, points[face.Vertex0], face);
                    if (dist > QuickHull.EPSILON) {
                        assigned = true;
                        fp.Face = fi;
                        fp.Distance = dist;
                        this.openSet[i] = fp;
                        break;
                    }
                }
                if (!assigned) {
                    fp.Face = QuickHull.INSIDE;
                    fp.Distance = Number.NaN;
                    this.openSet[i] = this.openSet[this.openSetTail];
                    this.openSet[this.openSetTail] = fp;
                    i--;
                    this.openSetTail--;
                }
            }
        }
    }
    _exportMeshData(points, splitVerts) {
        const verts = new Array();
        const tris = new Array();
        const normals = new Array();
        for (let face of this.faces.values()) {
            let vi0 = 0, vi1 = 0, vi2 = 0;
            if (splitVerts) {
                vi0 = verts.length;
                verts.push(points[face.Vertex0]);
                vi1 = verts.length;
                verts.push(points[face.Vertex1]);
                vi2 = verts.length;
                verts.push(points[face.Vertex2]);
                normals.push(face.Normal);
                normals.push(face.Normal);
                normals.push(face.Normal);
            }
            else {
                let tmp = this.hullVerts.get(face.Vertex0);
                if (!tmp) {
                    vi0 = verts.length;
                    this.hullVerts.set(face.Vertex0, vi0);
                    verts.push(points[face.Vertex0]);
                }
                else {
                    vi0 = tmp;
                }
                tmp = this.hullVerts.get(face.Vertex1);
                if (!tmp) {
                    vi1 = verts.length;
                    this.hullVerts.set(face.Vertex1, vi1);
                    verts.push(points[face.Vertex1]);
                }
                else {
                    vi1 = tmp;
                }
                tmp = this.hullVerts.get(face.Vertex2);
                if (!tmp) {
                    vi2 = verts.length;
                    this.hullVerts.set(face.Vertex2, vi2);
                    verts.push(points[face.Vertex2]);
                }
                else {
                    vi2 = tmp;
                }
            }
            tris.push(vi0);
            tris.push(vi1);
            tris.push(vi2);
        }
        return { vertices: verts, faces: tris, normals: normals };
    }
    _pointFaceDistance(point, pointOnFace, face) {
        return Cartesian3.Dot(face.Normal, Cartesian3.Subtract(point, pointOnFace));
    }
    _verifyOpenSet(points) {
        for (let i = 0; i < this.openSet.length; i++) {
            if (i > this.openSetTail) {
                Assert(this.openSet[i].Face == QuickHull.INSIDE);
            }
            else {
                Assert(this.openSet[i].Face != QuickHull.INSIDE);
                Assert(this.openSet[i].Face != QuickHull.UNASSIGNED);
                Assert(this._pointFaceDistance(points[this.openSet[i].Point], points[this.faces.get(this.openSet[i].Face).Vertex0], this.faces.get(this.openSet[i].Face)) > 0.0);
            }
        }
    }
    _verifyHorizon() {
        for (let i = 0; i < this.horizon.length; i++) {
            const prev = i == 0 ? this.horizon.length - 1 : i - 1;
            Assert(this.horizon[prev].Edge1 == this.horizon[i].Edge0);
            Assert(this._hasEdge(this.faces.get(this.horizon[i].Face), this.horizon[i].Edge1, this.horizon[i].Edge0));
        }
    }
    _verifyFaces(points) {
        for (var [fi, face] of this.faces) {
            Assert(this.faces.has(face.Opposite0));
            Assert(this.faces.has(face.Opposite1));
            Assert(this.faces.has(face.Opposite2));
            Assert(face.Opposite0 != fi);
            Assert(face.Opposite1 != fi);
            Assert(face.Opposite2 != fi);
            Assert(face.Vertex0 != face.Vertex1);
            Assert(face.Vertex0 != face.Vertex2);
            Assert(face.Vertex1 != face.Vertex2);
            Assert(this._hasEdge(this.faces.get(face.Opposite0), face.Vertex2, face.Vertex1));
            Assert(this._hasEdge(this.faces.get(face.Opposite1), face.Vertex0, face.Vertex2));
            Assert(this._hasEdge(this.faces.get(face.Opposite2), face.Vertex1, face.Vertex0));
            const tmp = Cartesian3.Subtract(face.Normal, Cartesian3.Normal(points[face.Vertex0], points[face.Vertex1], points[face.Vertex2]));
            Assert(Cartesian3.Magnitude(tmp) < QuickHull.EPSILON);
        }
    }
    _hasEdge(f, e0, e1) {
        if (f == undefined)
            return false;
        return (f.Vertex0 == e0 && f.Vertex1 == e1) || (f.Vertex1 == e0 && f.Vertex2 == e1) || (f.Vertex2 == e0 && f.Vertex0 == e1);
    }
}
QuickHull.UNASSIGNED = -2;
QuickHull.INSIDE = -1;
QuickHull.EPSILON = 0.0001;
//# sourceMappingURL=geometry.convex.quickhull.js.map